<script setup lang="ts">
import { socketEmitAsync } from '@/utils/web-socket'
import { soulClass, soulList } from './data'
import { useUserStore } from '@/stores/modules/user'
import RuneCollction from './components/RuneCollction.vue'
import RuneBless from './components/RuneBless.vue'
import SoulInfo from './components/SoulInfo.vue'
import { ref } from 'vue'
import type { RuneItemType } from '@/types/rune'
import { getRuneName } from '@/core/rune'
import RuneInfo from '../item/components/RuneInfo.vue'

const userStore = useUserStore()

const curTab = ref('bless')

const isChange = ref(false)
function handleChangeSoul() {
  if (userStore.diamond < 2000) return ElMessage.warning('转换战魂需 2000钻石')
  isChange.value = true
}

const showInitRune = ref(false)
const initRuneList = ref<RuneItemType[]>([])
function handleChoiceSoul(soulType: number, name: string) {
  ElMessageBox.confirm(
    `<p class="affix">确定选择<span class="${soulClass[soulType]}">【${name}】</span>作为您的战魂吗？${
      isChange.value ? '（本次选择花费 <span class="diamond">2000</span>钻石）' : ''
    }</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      const res = await socketEmitAsync<RuneItemType[]>({ type: 'setSoulType', data: { soulType } })
      if (res) {
        userStore.runeList.push(...res)
        initRuneList.value = res
        showInitRune.value = true
      }
      userStore.soulType = soulType
      if (isChange.value) {
        userStore.diamond -= 2000
        isChange.value = false
        return ElMessage.success('转换战魂成功')
      }
    })
    .catch(() => {})
}
</script>

<template>
  <div class="awaken-page page">
    <div class="choice-soul" v-if="!userStore.soulType || isChange || showInitRune">
      <h2>请选择您的战魂</h2>
      <div class="intro affix">
        <p>1. 首次觉醒可获得 <span class="grow">7</span>种对应战魂的符石</p>
        <p>2. 您更容易获取对应战魂所属的符石</p>
        <p>3. 携带战魂所属的符石时，可获得 <span class="grow">20%</span> 的属性加成</p>
        <p>4. 选择后可花费<span class="diamond">钻石</span>转换为其他战魂</p>
      </div>
      <div class="soul-list">
        <CommonBtn
          v-for="item of soulList.filter((item) => item.id !== userStore.soulType)"
          :type="item.type"
          style="text-align: left; width: 100%"
          @done="handleChoiceSoul(item.id, item.name)"
        >
          <div class="soul-item">
            <h4>{{ item.name }}</h4>
            <p>相关属性：{{ item.basicAttrs }}</p>
            <p>相关词条：{{ item.specialAttrs }}</p>
          </div>
        </CommonBtn>
      </div>
      <CommonBtn type="warning" @click="isChange = false" style="width: 100%; margin-top: 30px" v-show="isChange">取消转换</CommonBtn>

      <el-dialog v-model="showInitRune" width="100%" style="max-width: 500px">
        <h2 class="affix">
          <span class="grow">觉醒成功，获得以下初始符石奖励</span>
        </h2>
        <div class="btn-wrap" style="max-height: 50vh; overflow: auto">
          <el-row :gutter="10">
            <el-col :span="12" v-for="item of initRuneList" :key="item.id">
              <el-popover placement="bottom" width="300px" trigger="click">
                <template #reference>
                  <CommonBtn size="small" style="margin-bottom: 5px">
                    <span :class="item.quality">
                      {{ getRuneName(item.runeId) }}
                    </span>
                  </CommonBtn>
                </template>
                <RuneInfo :rune-info="item" :show-action="false" />
              </el-popover>
            </el-col>
          </el-row>
        </div>
        <template #footer>
          <p style="text-align: center">
            <el-button plain @click="showInitRune = false" size="small">关闭</el-button>
          </p>
        </template>
      </el-dialog>
    </div>

    <div class="awaken-palace" v-else>
      <h2>觉醒殿堂</h2>
      <el-tabs v-model="curTab" stretch>
        <el-tab-pane label="祈福" name="bless" class="pane" lazy>
          <RuneBless class="pane-item" />
        </el-tab-pane>
        <el-tab-pane label="图鉴" name="collection" class="pane" lazy>
          <RuneCollction class="pane-item" />
        </el-tab-pane>
        <el-tab-pane label="战魂" name="soulInfo" class="pane" lazy>
          <SoulInfo class="pane-item" @changeSoul="handleChangeSoul" />
        </el-tab-pane>
      </el-tabs>
    </div>

    <FooterBar />
  </div>
</template>

<style lang="scss" scoped>
.awaken-page {
  padding-top: 20px;
  display: flex;
  flex-flow: column nowrap;
  height: 100%;
  overflow: hidden;
  .intro {
    margin-bottom: 20px;
    p {
      font-size: 14px;
      margin: 8px 0;
    }
  }
  .el-tabs {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    :deep() {
      .el-tabs__content {
        flex: 1;
        .pane {
          height: 100%;
          .pane-item {
            height: 100%;
            overflow: hidden;
            display: flex;
          }
        }
      }
    }
  }
  .choice-soul {
    height: 100%;
    h2 {
      margin-top: 0;
    }
    .el-button {
      span {
        display: flex;
        flex-flow: column nowrap;
      }
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
    .soul-item {
      h4 {
        text-align: left;
        margin: 0 0 10px 0;
      }
      p {
        font-size: 12px;
        margin: 4px 0;
      }
    }
  }
  .awaken-palace {
    display: flex;
    flex-flow: column nowrap;
    height: 100%;
    overflow: hidden;
  }
  .btn-wrap > div {
    align-items: flex-start;
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
  }
}
</style>
